import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, View, type ViewStyle } from "react-native";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react";
import { AnimatePresence, MotiView } from "moti";
import {
  BookOpenIcon,
  CalculatorIcon,
  CaretDownIcon,
  CheckIcon,
  ImageSquareIcon,
  MagnifyingGlassIcon,
  QuestionIcon,
  SlidersHorizontalIcon,
  SpeakerSimpleHighIcon,
} from "@phosphor-icons/react";
import { Portal } from "react-native-paper";
import CustomSwitch from "../../../common/general/CustomSwitch/CustomSwitch";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, spacing, typographyScale, webTransitions } from "../../../../styles/design-system";
import { type ChatToolContext, type ToolId, type ToolOption } from "./toolOptions";
import { type PlaybackSpeed } from "../../../../utils/toolPreferenceStorage";
import { useFocusTrap } from "../../../../hooks/useFocusTrap";

interface VoiceSettingsState {
  autoDictate: boolean;
}

interface ToolsMenuProps {
  chatContext: ChatToolContext;
  isDisabled: boolean;
  isAudioOn: boolean;
  onAudioOnPress: () => void;
  onAudioOffPress: () => void;
  tools: ToolOption[];
  activeToolId: ToolId | null;
  onToolSelect: (toolId: ToolId) => void;
  mathModeEnabled: boolean;
  onMathModeToggle: (enabled: boolean) => void;
  playbackSpeed: PlaybackSpeed;
  onPlaybackSpeedChange: (speed: PlaybackSpeed) => void;
}

const PLAYBACK_SPEEDS: PlaybackSpeed[] = [0.5, 0.75, 1, 1.25, 1.5, 2];
const MATH_MODE_LABEL = "Math Mode";
const TOOLS_MENU_CONFIG_BY_CHAT: Record<
  ChatToolContext,
  {
    enableToolSelection: boolean;
    enableMathMode: boolean;
  }
> = {
  companion: {
    enableToolSelection: true,
    enableMathMode: true,
  },
  ile: {
    enableToolSelection: false,
    enableMathMode: true,
  },
};

const getToolIcon = (toolId: ToolId, isActive: boolean) => {
  const color = isActive ? colors.brand[600] : colors.grey[500];

  switch (toolId) {
    case "guidedLearning":
      return <BookOpenIcon size={20} color={color} weight="regular" />;
    case "quizMe":
      return <QuestionIcon size={20} color={color} weight="regular" />;
    case "deepResearch":
      return <MagnifyingGlassIcon size={20} color={color} weight="regular" />;
    case "createImage":
      return <ImageSquareIcon size={20} color={color} weight="regular" />;
    default:
      return null;
  }
};

interface ToggleMenuRowProps {
  label: string;
  value: boolean;
  onToggle: (enabled: boolean) => void;
  accessibilityLabel: string;
  disabled?: boolean;
  leftContent?: React.ReactNode;
}

const ToggleMenuRow: React.FC<ToggleMenuRowProps> = ({
  label,
  value,
  onToggle,
  accessibilityLabel,
  disabled = false,
  leftContent,
}) => {
  return (
    <Pressable
      role="button"
      disabled={disabled}
      onPress={() => onToggle(!value)}
      style={(state) => {
        const hovered = (state as { hovered?: boolean }).hovered;
        return [
          styles.toggleMenuItem,
          disabled && styles.toggleMenuItemDisabled,
          !disabled && hovered && styles.menuItemHovered,
          !disabled && state.pressed && styles.menuItemPressed,
        ];
      }}>
      <View style={styles.toggleMenuLabelGroup}>
        {leftContent}
        <CustomText text={label} useTranslationText={false} style={styles.toggleMenuItemText} />
      </View>
      <CustomSwitch
        value={value}
        onValueChange={onToggle}
        onPress={(event) => {
          event.stopPropagation();
        }}
        size="s"
        disabled={disabled}
        accessibilityLabel={accessibilityLabel}
      />
    </Pressable>
  );
};

const ToolsMenu: React.FC<ToolsMenuProps> = ({
  chatContext,
  isDisabled,
  isAudioOn,
  onAudioOnPress,
  onAudioOffPress,
  tools,
  activeToolId,
  onToolSelect,
  mathModeEnabled,
  onMathModeToggle,
  playbackSpeed,
  onPlaybackSpeedChange,
}) => {
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettingsState>({
    autoDictate: false,
  });

  const { refs, x, y, strategy, placement } = useFloating({
    placement: "top-start",
    middleware: [offset(spacing.xs), flip(), shift({ padding: spacing.sm })],
    whileElementsMounted: autoUpdate,
  });

  const closeToolsMenu = useCallback(() => {
    setIsToolsMenuOpen(false);
  }, []);

  const floatingTrapRef = useRef<View | null>(null);
  useFocusTrap(floatingTrapRef, { visible: isToolsMenuOpen, onEscape: closeToolsMenu });

  const handleToolsMenuToggle = useCallback(() => {
    if (isToolsMenuOpen) {
      closeToolsMenu();
      return;
    }
    setIsToolsMenuOpen(true);
  }, [closeToolsMenu, isToolsMenuOpen]);

  useEffect(() => {
    setVoiceSettings((prev) => ({
      ...prev,
      autoDictate: isAudioOn ? prev.autoDictate : false,
    }));
  }, [isAudioOn]);

  const handleAutoReadAloudChange = (enabled: boolean) => {
    if (enabled) {
      onAudioOnPress();
    } else {
      onAudioOffPress();
    }

    if (!enabled) {
      setVoiceSettings((prev) => ({
        ...prev,
        autoDictate: false,
      }));
    }
  };

  const handleAutoDictateChange = (enabled: boolean) => {
    if (!isAudioOn) {
      return;
    }

    setVoiceSettings((prev) => ({
      ...prev,
      autoDictate: enabled,
    }));
  };

  const handlePlaybackSpeedChange = (speed: PlaybackSpeed) => {
    onPlaybackSpeedChange(speed);
  };

  const isToolsTriggerDisabled = isDisabled && !isToolsMenuOpen;
  const toolsMenuConfig = TOOLS_MENU_CONFIG_BY_CHAT[chatContext];
  const activeTool =
    toolsMenuConfig.enableToolSelection && activeToolId
      ? (tools.find((tool) => tool.id === activeToolId) ?? null)
      : null;
  const shouldShowToolsSection = toolsMenuConfig.enableToolSelection && tools.length > 0;
  const shouldShowMathModeSection = toolsMenuConfig.enableMathMode;

  const openUpward = placement.startsWith("top");

  return (
    <View style={styles.toolsMenuContainer} ref={refs.setReference as unknown as React.Ref<View>}>
      <Pressable
        onPress={handleToolsMenuToggle}
        disabled={isToolsTriggerDisabled}
        role="button"
        accessibilityLabel={isToolsMenuOpen ? "Close tools menu" : "Open tools menu"}
        style={(state) => {
          const hovered = (state as { hovered?: boolean }).hovered;
          return [
            styles.toolsTriggerButton,
            activeTool && styles.toolsTriggerButtonActive,
            !isToolsTriggerDisabled && hovered && !activeTool && styles.toolsTriggerButtonHovered,
            !isToolsTriggerDisabled && hovered && activeTool && styles.toolsTriggerButtonActiveHovered,
            !isToolsTriggerDisabled && state.pressed && styles.toolsTriggerButtonPressed,
            isToolsTriggerDisabled && styles.iconButtonDisabled,
          ];
        }}>
        {activeTool ? (
          <>
            {getToolIcon(activeTool.id, true)}
            <CustomText
              text={activeTool.label}
              useTranslationText={false}
              style={[styles.toolsTriggerText, styles.toolsTriggerTextActive]}
            />
          </>
        ) : (
          <>
            <SlidersHorizontalIcon size={16} color={colors.grey[600]} weight="bold" />
            <CustomText text="Tools" useTranslationText={false} style={styles.toolsTriggerText} />
          </>
        )}
        <CaretDownIcon size={12} color={activeTool ? colors.brand[400] : colors.grey[400]} weight="bold" />
      </Pressable>

      <Portal>
        {isToolsMenuOpen ? (
          <Pressable style={styles.toolsMenuOverlay} onPress={closeToolsMenu} accessibilityLabel="Close tools menu" />
        ) : null}
        <AnimatePresence>
          {isToolsMenuOpen ? (
            <View
              ref={(node) => {
                (refs.setFloating as unknown as (node: View | null) => void)(node);
                floatingTrapRef.current = node;
              }}
              role="dialog"
              aria-label="Tools menu"
              accessibilityRole="none"
              accessibilityLabel="Tools menu"
              style={[
                styles.toolsMenuPosition,
                {
                  position: (strategy === "fixed" ? "absolute" : strategy) as ViewStyle["position"],
                  left: x ?? 0,
                  top: y ?? 0,
                },
              ]}>
              <MotiView
                key="tools-menu"
                style={styles.toolsMenu}
                from={{
                  opacity: 0,
                  translateY: openUpward ? 8 : -8,
                  scale: 0.98,
                }}
                animate={{ opacity: 1, translateY: 0, scale: 1 }}
                exit={{
                  opacity: 0,
                  translateY: openUpward ? 8 : -8,
                  scale: 0.98,
                }}
                transition={{ type: "timing", duration: 180 }}>
                {/* Tools section */}
                {shouldShowToolsSection && (
                  <View style={styles.menuSection}>
                    <View style={styles.menuSectionRowHeader}>
                      <CustomText text="Tools" useTranslationText={false} style={styles.menuSectionHeaderText} />
                    </View>

                    <View style={styles.toolsGrid}>
                      {tools.map((tool) => {
                        const isActiveTool = activeToolId === tool.id;

                        return (
                          <Pressable
                            key={tool.id}
                            role="button"
                            onPress={() => {
                              onToolSelect(tool.id);
                              closeToolsMenu();
                            }}
                            style={(state) => {
                              const hovered = (state as { hovered?: boolean }).hovered;
                              return [
                                styles.toolMenuItem,
                                isActiveTool && styles.toolMenuItemActive,
                                !isActiveTool && hovered && styles.menuItemHovered,
                                state.pressed && styles.menuItemPressed,
                              ];
                            }}>
                            {getToolIcon(tool.id, isActiveTool)}
                            <CustomText
                              text={tool.label}
                              useTranslationText={false}
                              style={[styles.toolMenuItemText, isActiveTool && styles.toolMenuItemTextActive]}
                            />
                            {isActiveTool ? <CheckIcon size={14} color={colors.brand[600]} weight="bold" /> : null}
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>
                )}

                {/* Math mode section */}
                {shouldShowMathModeSection ? (
                  <>
                    {shouldShowToolsSection ? <View style={styles.menuSeparator} /> : null}
                    <View style={styles.menuSection}>
                      <ToggleMenuRow
                        label={MATH_MODE_LABEL}
                        value={mathModeEnabled}
                        onToggle={onMathModeToggle}
                        accessibilityLabel="Math mode toggle"
                        leftContent={
                          <CalculatorIcon
                            size={20}
                            color={mathModeEnabled ? colors.brand[600] : colors.grey[500]}
                            weight="regular"
                          />
                        }
                      />
                    </View>
                  </>
                ) : null}

                {shouldShowToolsSection || shouldShowMathModeSection ? <View style={styles.menuSeparator} /> : null}
                <View style={styles.menuSection}>
                  <View style={styles.menuSectionRowHeader}>
                    <SpeakerSimpleHighIcon size={14} color={colors.grey[600]} weight="regular" />
                    <CustomText text="Voice" useTranslationText={false} style={styles.menuSectionHeaderText} />
                  </View>

                  <ToggleMenuRow
                    label="Auto Read Aloud"
                    value={isAudioOn}
                    onToggle={handleAutoReadAloudChange}
                    accessibilityLabel="Auto read aloud toggle"
                  />

                  {/*
                    <ToggleMenuRow
                      label="Auto Dictate"
                      value={voiceSettings.autoDictate}
                      onToggle={handleAutoDictateChange}
                      disabled={!isAudioOn}
                      accessibilityLabel="Auto dictate toggle"
                    />
                  */}
                </View>

                {/* Playback speed section */}
                <View style={[styles.menuSection, styles.menuSectionInset, styles.menuSectionCompact]}>
                  <CustomText text="Playback Speed" useTranslationText={false} style={styles.playbackLabel} />
                  <View style={styles.playbackButtonRow}>
                    {PLAYBACK_SPEEDS.map((speed) => {
                      const isActiveSpeed = playbackSpeed === speed;

                      return (
                        <Pressable
                          key={speed}
                          role="button"
                          onPress={() => handlePlaybackSpeedChange(speed)}
                          style={(state) => {
                            const hovered = (state as { hovered?: boolean }).hovered;
                            return [
                              styles.playbackSpeedButton,
                              isActiveSpeed && styles.playbackSpeedButtonActive,
                              !isActiveSpeed && hovered && styles.playbackSpeedButtonHovered,
                              state.pressed && styles.playbackSpeedButtonPressed,
                            ];
                          }}>
                          <CustomText
                            text={`${speed}x`}
                            useTranslationText={false}
                            style={[styles.playbackSpeedText, isActiveSpeed && styles.playbackSpeedTextActive]}
                          />
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              </MotiView>
            </View>
          ) : null}
        </AnimatePresence>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  toolsMenuContainer: {
    position: "relative",
    zIndex: 10,
  },
  toolsTriggerButton: {
    minWidth: 104,
    height: 36,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing.xxs,
    borderRadius: radii.full,
    paddingVertical: 0,
    paddingHorizontal: spacing.xs,
    ...webTransitions.fast,
  },
  toolsTriggerButtonHovered: {
    backgroundColor: colors.grey[200],
  },
  toolsTriggerButtonActive: {
    backgroundColor: colors.brand[50],
  },
  toolsTriggerButtonActiveHovered: {
    backgroundColor: colors.brand[100],
  },
  toolsTriggerButtonPressed: {
    opacity: 0.8,
  },
  toolsTriggerText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: colors.grey[700],
  },
  toolsTriggerTextActive: {
    color: colors.brand[700],
  },
  iconButtonDisabled: {
    opacity: 0.5,
  },
  toolsMenuOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "transparent",
    zIndex: 29,
  },
  toolsMenuPosition: {
    zIndex: 30,
  },
  toolsMenu: {
    minWidth: 360,
    width: 360,
    padding: spacing.xs,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
    backgroundColor: colors.white,
    shadowColor: colors.grey[900],
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.1,
    shadowRadius: 16,
    elevation: 6,
  },
  menuSection: {
    gap: spacing.xxs,
  },
  menuSectionInset: {
    paddingHorizontal: spacing.xs,
  },
  menuSectionCompact: {
    paddingTop: spacing.xs,
    paddingBottom: spacing.xxs,
  },
  menuSectionRowHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
    paddingHorizontal: spacing.xs,
    paddingTop: spacing.xxs,
    paddingBottom: spacing.xxs,
  },
  menuSectionHeaderText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
    color: colors.grey[500],
  },
  toggleMenuItem: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: radii.lg,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    ...webTransitions.fast,
  },
  menuItemHovered: {
    backgroundColor: colors.grey[50],
  },
  menuItemPressed: {
    opacity: 0.9,
  },
  toggleMenuItemDisabled: {
    opacity: 0.5,
  },
  toggleMenuItemText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: colors.grey[900],
  },
  playbackLabel: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
    color: colors.grey[500],
  },
  playbackButtonRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  playbackSpeedButton: {
    flex: 1,
    minHeight: 32,
    borderRadius: radii.sm,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: spacing.xxs,
    backgroundColor: colors.grey[100],
    ...webTransitions.fast,
  },
  playbackSpeedButtonActive: {
    backgroundColor: colors.brand[500],
  },
  playbackSpeedButtonHovered: {
    backgroundColor: colors.grey[200],
  },
  playbackSpeedButtonPressed: {
    opacity: 0.9,
  },
  playbackSpeedText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "600",
    color: colors.grey[600],
  },
  playbackSpeedTextActive: {
    color: colors.white,
  },
  toolsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: spacing.xxs,
  },
  toolMenuItem: {
    width: "49%",
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: radii.lg,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
    gap: spacing.xxs,
    ...webTransitions.fast,
  },
  toolMenuItemActive: {
    backgroundColor: colors.brand[50],
  },
  toolMenuItemText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    fontWeight: "500",
    color: colors.grey[900],
    flex: 1,
  },
  toolMenuItemTextActive: {
    color: colors.brand[700],
  },
  toggleMenuLabelGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
    flex: 1,
  },
  menuSeparator: {
    height: 1,
    backgroundColor: colors.grey[100],
    marginVertical: spacing.xs,
  },
});

export default ToolsMenu;
